import type { AxiosInstance } from 'axios';
import { unwrap, type ObjectResponse } from '../core/envelope';

// Re-export the shared envelope shape so any external consumer importing
// `ObjectResponse` from this file keeps working unchanged.
export type { ObjectResponse } from '../core/envelope';

/**
 * Multi-tenant login payload — mirrors the live `LoginRequest` schema at
 * `https://api.ebillbook.kristysoftware.com/swagger/v1/swagger.json`.
 *
 * - `tenantCode` (optional, max 60) — explicit tenant short code.
 * - `userName` (required, max 80) — credential username (camelCase per spec).
 * - `password` (required, max 128).
 *
 * The backend resolves the tenant in this priority: `tenantCode` →
 * domain → subdomain → host lookup via `TenantDomainMap`.
 */
export interface LoginPayload {
  tenantCode?: string;
  userName: string;
  password: string;
}

/**
 * Refresh-token rotation payload. The live spec requires **both** the
 * refresh token and the session id — sessions are tracked server-side and
 * the pair must match for reuse-detection to work.
 */
export interface RefreshPayload {
  refreshToken: string;
  sessionId: string;
}

/** Logout / revoke. The backend invalidates by session id, not token. */
export interface RevokeRequest {
  sessionId: string;
}

export interface RegisterPayload {
  tenantCode?: string;
  storeId?: string;
  userName: string;
  email: string;
  phone?: string;
  password: string;
  firstName: string;
  lastName?: string;
}

export interface ForgotPasswordPayload {
  tenantCode?: string;
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
  confirmPassword?: string;
}

/**
 * Profile fields the frontend needs after login. The live API does **not**
 * include these in the login response body — they live in the JWT claims.
 * Use `decodeJwt(accessToken)` from `@kss/auth` to populate this shape.
 */
export interface LoginUserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  permissions?: string[];
}

/**
 * Flat login response payload — exact shape returned by
 * `POST /api/auth/Login` (verified live 2026-04-28).
 *
 * NOTE: there is **no** nested `user` object. Display name / email / role /
 * permission codes are JWT claims and must be decoded client-side.
 */
export interface AuthTokenBundle {
  accessToken: string;
  refreshToken: string;
  /** Absolute ISO datetime when accessToken expires (~15 min after issue). */
  expiresAt: string;
  tenantId: string;
  /**
   * Short tenant code (e.g. "bill"). The login response returns it on
   * the flat envelope (verified 2026-05-16). Captured here so the
   * silent-refresh interceptor in `client.ts` can pass it back to
   * `/api/Auth/refresh` — the live endpoint requires it (§25).
   */
  tenantCode?: string;
  sessionId: string;
  userId: string;
  userName: string;
}

/** Alias — login and refresh both resolve the same flat envelope payload. */
export type LoginResponse = AuthTokenBundle;

export function createAuthService(client: AxiosInstance) {
  return {
    /** `POST /api/Auth/login` (verified live 2026-05-05). */
    login: (payload: LoginPayload) =>
      client
        .post<ObjectResponse<LoginResponse>>('/Auth/login', payload)
        .then((r) => unwrap(r.data)),

    /**
     * `POST /api/Auth/refresh` — rotates the refresh-token chain.
     * Live URL is `/Auth/refresh` (not `/refresh-token`) and requires
     * `tenantCode` in the body — verified 2026-05-16. The §16 contract
     * spec'd `/refresh-token`; the live server only mounts `/refresh`.
     * Tracked as §25 in BACKEND_AGENT_PROMPTS.md.
     */
    refresh: (payload: RefreshPayload) =>
      client
        .post<ObjectResponse<AuthTokenBundle>>('/Auth/refresh', payload)
        .then((r) => unwrap(r.data)),

    /** `POST /api/Auth/logout` — server-side logout / session invalidation. */
    revoke: (payload: RevokeRequest) =>
      client.post<ObjectResponse<unknown>>('/Auth/logout', payload).then((r) => r.data),

    /**
     * `POST /api/Auth/logout` — alias kept for the legacy `logout()`
     * callsite in `useSignOut`. Pass the active sessionId via `payload.sessionId`.
     * The live endpoint is `/Auth/logout` (verified 200 on 2026-05-16);
     * the previous `/Auth/revoke-token` URL is not mounted.
     */
    logout: (payload?: RevokeRequest) =>
      payload?.sessionId
        ? client.post<ObjectResponse<unknown>>('/Auth/logout', payload).then(() => undefined)
        : Promise.resolve(undefined),

    /**
     * `GET /api/auth/session/{sessionId}` — confirms the live session is
     * still active server-side. Returns the session metadata; the actual
     * user identity must be decoded from the JWT (no profile fields here).
     */
    session: (sessionId: string) =>
      client
        .get<ObjectResponse<{
          sessionId: string;
          active: boolean;
          info?: {
            sessionId: string;
            userId: string;
            tenantId: string;
            userName: string;
            refreshToken?: string;
            issuedAt?: string;
            absoluteExpiresAt?: string;
            refreshExpiresAt?: string;
            lastUsedAt?: string;
            isRevoked?: boolean;
            revokedAt?: string | null;
            ip?: string;
            userAgent?: string;
          };
        }>>(`/auth/session/${encodeURIComponent(sessionId)}`)
        .then((r) => unwrap(r.data)),

    /** `POST /api/auth/register` */
    register: (payload: RegisterPayload) =>
      client.post<ObjectResponse<LoginResponse>>('/auth/register', payload).then((r) => unwrap(r.data)),

    /**
     * `POST /api/Auth/get-tenants` — discover which tenants an email /
     * phone is registered with. Used by the tri-modal login's "I don't
     * know my company" path. Verified live 2026-05-05.
     */
    getTenants: (payload: { identifier: string }) =>
      client
        .post<ObjectResponse<Array<{ tenantId: string; tenantCode: string; tenantName: string; logoUrl?: string | null }>>>(
          '/Auth/get-tenants',
          payload,
        )
        .then((r) => unwrap(r.data)),

    /**
     * `POST /api/Auth/send-otp` — kicks off passwordless sign-in.
     *
     * The live endpoint requires **`tenantCode`** + `identifier`
     * (verified 2026-06-01: sending only `tenantId` → 400 "TenantCode and
     * Identifier are required."). `tenantId` is accepted but ignored. So
     * `tenantCode` is required; `tenantId` is optional.
     * Demo mode returns the OTP `code` inline in the response.
     */
    sendOtp: (payload: { identifier: string; purpose: 'Login' | 'Reset' | 'Verify'; tenantCode: string; tenantId?: string }) =>
      client
        .post<ObjectResponse<{ otpRequestId?: string; expiresAt?: string; code?: string }>>('/Auth/send-otp', payload)
        .then((r) => unwrap(r.data)),

    /**
     * `POST /api/Auth/verify-otp` — exchanges an OTP for an AuthResponse.
     * Requires `tenantCode` + `identifier` + **`code`** (verified live
     * 2026-06-01: `otpCode` → 400 "TenantCode, Identifier and Code are
     * required."). We accept `otpCode` in the FE signature for convenience
     * and forward it as `code`.
     */
    verifyOtp: (payload: { identifier: string; otpCode: string; tenantCode: string; tenantId?: string }) =>
      client
        .post<ObjectResponse<LoginResponse>>('/Auth/verify-otp', {
          tenantCode: payload.tenantCode,
          tenantId: payload.tenantId,
          identifier: payload.identifier,
          code: payload.otpCode,
        })
        .then((r) => unwrap(r.data)),

    /** `POST /api/Auth/forgot-password` (verified live 2026-05-05). */
    forgotPassword: (payload: ForgotPasswordPayload) =>
      client.post<ObjectResponse<unknown>>('/Auth/forgot-password', payload).then((r) => r.data),

    /** `POST /api/auth/reset-password` */
    resetPassword: (payload: ResetPasswordPayload) =>
      client.post<ObjectResponse<unknown>>('/auth/reset-password', payload).then((r) => r.data),

    /** `POST /api/auth/verify-email` */
    verifyEmail: (token: string) =>
      client.post<ObjectResponse<unknown>>('/auth/verify-email', { token }).then((r) => r.data),

    /** `POST /api/auth/send-verification` */
    sendVerification: (email: string, tenantCode?: string) =>
      client
        .post<ObjectResponse<unknown>>('/auth/send-verification', { email, tenantCode })
        .then((r) => r.data),

    /**
     * `GET /api/Auth/me` — returns the server's view of the current
     * JWT's auth state. Verified live 2026-05-16:
     *
     *   {
     *     "isAuthenticated": true,
     *     "username":   "admin",
     *     "tenantId":   "...",
     *     "sessionId":  "...",
     *     "expiration": "yyyyMMddHHmmss",
     *     "roles":      ["Admin","SuperAdmin"],
     *     "allClaims":  [...]
     *   }
     *
     * Used by `useSessionBootstrap` to detect server-side session
     * revocation (e.g. user logged in from another device → server
     * issues a new sessionId; this tab's sessionId no longer matches
     * → force logout).
     */
    me: () =>
      client
        .get<{
          isAuthenticated?: boolean;
          username?: string;
          tenantId?: string;
          sessionId?: string;
          expiration?: string;
          roles?: string[];
        }>('/Auth/me')
        .then((r) => r.data),
  };
}

export type AuthService = ReturnType<typeof createAuthService>;
